/**
 * The Mann–Whitney U test (Wilcoxon rank-sum test) for two independent samples.
 *
 * Matches `scipy.stats.mannwhitneyu` with `method="exact"` and `method="asymptotic"`.
 * U₁ = R₁ − n₁(n₁ + 1)/2 counts the pairs in which the first sample's value is larger
 * (ties count ½); U₂ = n₁n₂ − U₁. R's `wilcox.test` reports U₁ as "W"; SPSS reports
 * the smaller of the two.
 *
 * The exact null distribution of U (no ties) is built by the recurrence
 * c(u; m, n) = c(u − n; m − 1, n) + c(u; m, n − 1). The normal approximation uses the
 * tie-corrected variance σ² = n₁n₂/12 · [(N + 1) − Σ(t³ − t) / (N(N − 1))].
 */

import { averageRanks, tieGroupSizes } from "@/lib/stats/descriptive";
import { normalCdf, normalUpper } from "@/lib/stats/distributions";
import { logChoose } from "@/lib/stats/special";
import type { Alternative } from "@/lib/tool-kit/stats-tests/format";

/** Exact p-values are offered up to this size per group, when there are no ties. */
export const EXACT_LIMIT = 20;

export interface MannWhitneyResult {
  n1: number;
  n2: number;
  ranks1: number[];
  ranks2: number[];
  rankSum1: number;
  rankSum2: number;
  meanRank1: number;
  meanRank2: number;
  u1: number;
  u2: number;
  /** min(U₁, U₂). */
  u: number;
  mu: number;
  sigma: number;
  /** z from the smaller U — never positive — with the continuity correction if asked. */
  z: number;
  hasTies: boolean;
  /** Exact p for the chosen alternative, or null when ties or size rule it out. */
  pExact: number | null;
  pNormal: number;
  /** The p the result is reported with: exact when available. */
  p: number;
  method: "exact" | "normal";
  /** |z| / √N. */
  r: number;
  /** (U₁ − U₂) / (n₁n₂): positive when the first sample tends to be larger. */
  rankBiserial: number;
}

/** Counts of each U from 0 to mn, by dynamic programming over (m, n). */
export function uDistributionCounts(m: number, n: number): number[] {
  // counts[j][u] for the current m and every n' ≤ n; start from m = 0.
  let previous = Array.from({ length: n + 1 }, () => [1]);
  for (let i = 1; i <= m; i++) {
    const current: number[][] = [];
    current[0] = [1];
    for (let j = 1; j <= n; j++) {
      const size = i * j + 1;
      const row = new Array<number>(size).fill(0);
      const left = current[j - 1];
      const down = previous[j];
      for (let u = 0; u < size; u++) {
        row[u] =
          (u < left.length ? left[u] : 0) +
          (u - j >= 0 && u - j < down.length ? down[u - j] : 0);
      }
      current[j] = row;
    }
    previous = current;
  }
  return previous[n];
}

function exactP(
  u1: number,
  n1: number,
  n2: number,
  alternative: Alternative
): number {
  const counts = uDistributionCounts(n1, n2);
  const total = Math.exp(logChoose(n1 + n2, n1));
  const atLeast = (k: number) => {
    let sum = 0;
    for (let u = Math.max(0, Math.ceil(k)); u < counts.length; u++) {
      sum += counts[u];
    }
    return sum / total;
  };
  const u2 = n1 * n2 - u1;
  if (alternative === "greater") {
    return atLeast(u1);
  }
  if (alternative === "less") {
    return atLeast(u2);
  }
  return Math.min(1, 2 * atLeast(Math.max(u1, u2)));
}

function normalP(
  u1: number,
  mu: number,
  sigma: number,
  continuity: boolean,
  alternative: Alternative
): number {
  const cc = continuity ? 0.5 : 0;
  if (alternative === "greater") {
    return normalUpper((u1 - mu - cc) / sigma);
  }
  if (alternative === "less") {
    return normalCdf((u1 - mu + cc) / sigma);
  }
  const u = Math.max(u1, 2 * mu - u1);
  return Math.min(1, 2 * normalUpper((u - mu - cc) / sigma));
}

export function mannWhitney(
  x: readonly number[],
  y: readonly number[],
  options: { continuity: boolean; alternative: Alternative }
): MannWhitneyResult | null {
  const n1 = x.length;
  const n2 = y.length;
  if (n1 < 2 || n2 < 2) {
    return null;
  }
  const combined = [...x, ...y];
  const ranks = averageRanks(combined);
  const ranks1 = ranks.slice(0, n1);
  const ranks2 = ranks.slice(n1);
  const rankSum1 = ranks1.reduce((acc, r) => acc + r, 0);
  const rankSum2 = ranks2.reduce((acc, r) => acc + r, 0);
  const u1 = rankSum1 - (n1 * (n1 + 1)) / 2;
  const u2 = n1 * n2 - u1;
  const n = n1 + n2;
  const ties = tieGroupSizes(combined);
  const tieTerm = ties.reduce((acc, t) => acc + t ** 3 - t, 0);
  const mu = (n1 * n2) / 2;
  const sigma = Math.sqrt(((n1 * n2) / 12) * (n + 1 - tieTerm / (n * (n - 1))));
  if (sigma === 0) {
    return null;
  }
  const u = Math.min(u1, u2);
  const cc = options.continuity && u < mu ? 0.5 : 0;
  const z = (u - mu + cc) / sigma;
  const hasTies = ties.length > 0;
  const exactAllowed = !hasTies && n1 <= EXACT_LIMIT && n2 <= EXACT_LIMIT;
  const pExact = exactAllowed ? exactP(u1, n1, n2, options.alternative) : null;
  const pNormal = normalP(
    u1,
    mu,
    sigma,
    options.continuity,
    options.alternative
  );
  return {
    n1,
    n2,
    ranks1,
    ranks2,
    rankSum1,
    rankSum2,
    meanRank1: rankSum1 / n1,
    meanRank2: rankSum2 / n2,
    u1,
    u2,
    u,
    mu,
    sigma,
    z,
    hasTies,
    pExact,
    pNormal,
    p: pExact ?? pNormal,
    method: pExact === null ? "normal" : "exact",
    r: Math.abs(z) / Math.sqrt(n),
    rankBiserial: (u1 - u2) / (n1 * n2),
  };
}
